import { VIZ_KINDS, type VizEvent, type VizKind, type VizShow, type VizTrace } from './types';

/** viz 行の接頭辞（viz-protocol.md §3）。 */
export const VIZ_PREFIX = '@@viz ';
/** 1 つの stderr で読む viz 行の上限（viz-protocol.md §5 手順 6）。 */
export const MAX_VIZ_LINES = 5000;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v);
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isVizKind(v: unknown): v is VizKind {
  return typeof v === 'string' && (VIZ_KINDS as readonly string[]).includes(v);
}

function toMarks(v: unknown): Record<string, number> | null {
  if (!isRecord(v)) return null;
  const out: Record<string, number> = {};
  for (const [k, x] of Object.entries(v)) {
    if (!isInt(x)) return null;
    out[k] = x;
  }
  return out;
}

function toShow(o: Record<string, unknown>): VizShow | null {
  const { step, ticks, label, kind } = o;
  if (!isInt(step) || step < 1 || !isFiniteNumber(ticks) || typeof label !== 'string') return null;
  if (!isVizKind(kind) || !('value' in o)) return null;
  const show: VizShow = { t: 'show', step, ticks, label, kind, value: o['value'] };
  if (o['head'] !== undefined) {
    const head = o['head'];
    if (!isInt(head)) return null;
    show.head = head;
  }
  if (o['marks'] !== undefined) {
    const marks = toMarks(o['marks']);
    if (!marks) return null;
    show.marks = marks;
  }
  return show;
}

/**
 * sandboxd の `runs[i].stderr` から `@@viz` 行を拾う（viz-protocol.md §5）。
 *
 * - 行頭が `@@viz ` の行だけを viz 行とみなす。それ以外は `userStderr` に元の順で残す。
 * - 壊れた JSON・オブジェクトでない・`v !== 1`・必須フィールドの型違いは `malformed` に数える。
 * - 未知の `t` は黙って捨てる。viz 行は最大 {@link MAX_VIZ_LINES} 行まで読む。
 */
export function parseVizStderr(stderr: string): VizTrace {
  const events: VizEvent[] = [];
  const userLines: string[] = [];
  let malformed = 0;
  let truncated = false;
  let finalTotal: number | null = null;
  let partialMax: number | null = null;
  let vizLines = 0;

  for (const raw of stderr.split('\n')) {
    const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw;
    if (!line.startsWith(VIZ_PREFIX)) {
      userLines.push(line);
      continue;
    }
    vizLines += 1;
    if (vizLines > MAX_VIZ_LINES) {
      truncated = true;
      continue;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(line.slice(VIZ_PREFIX.length));
    } catch {
      malformed += 1;
      continue;
    }
    if (!isRecord(parsed) || parsed['v'] !== 1) {
      malformed += 1;
      continue;
    }
    switch (parsed['t']) {
      case 'show': {
        const show = toShow(parsed);
        if (show) events.push(show);
        else malformed += 1;
        break;
      }
      case 'note': {
        const { step, ticks, text } = parsed;
        if (isInt(step) && step >= 1 && isFiniteNumber(ticks) && typeof text === 'string') {
          events.push({ t: 'note', step, ticks, text });
        } else {
          malformed += 1;
        }
        break;
      }
      case 'ticks': {
        const { total, final } = parsed;
        if (!isFiniteNumber(total) || typeof final !== 'boolean') {
          malformed += 1;
        } else if (final) {
          finalTotal = total;
        } else if (partialMax === null || total > partialMax) {
          partialMax = total;
        }
        break;
      }
      case 'truncated':
        truncated = true;
        break;
      default:
        // 未知の t は前方互換のため黙って捨てる（§5 手順 3）。
        break;
    }
  }

  events.sort((a, b) => a.step - b.step);
  let ticks: VizTrace['ticks'] = null;
  if (finalTotal !== null) ticks = { total: finalTotal, final: true };
  else if (partialMax !== null) ticks = { total: partialMax, final: false };

  return { events, ticks, truncated, malformed, userStderr: userLines.join('\n') };
}

/** `@@viz` 行を取り除いた stderr（judge.md §3.1 の CaseResult.stderr）。 */
export function stripVizLines(stderr: string): string {
  return parseVizStderr(stderr).userStderr;
}
